import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { UserOption } from 'modules';
import { InfiniteDebouncedAutocomplete } from 'components';

import useNotifications from 'hooks/common/useNotifications';

import { searchUsers } from 'services/users.service';
import extractUsername from './utils/extractUsername';

import { DefaultPagination } from 'constants/pagination';

import { UserSearchType } from 'types/user';
import { IUsersAutocompleteProps } from './UsersAutocomplete.types';
import { IInfiniteDebounecedAutocompleteSearchParams } from 'components/inputs/InfiniteDebouncedAutocomplete';

const PAGE_SIZE = 20;

const UsersAutocomplete = ({
  value,
  placeholder,
  fullUser,
  onChange,
  onSelect,
  onClear,
  ...props
}: IUsersAutocompleteProps) => {
  const username = extractUsername(value);

  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState(username);
  const [options, setOptions] = useState([]);

  const { t } = useTranslation();
  const { notificationApi } = useNotifications();

  const generateOption = (user: UserSearchType) => {
    return {
      value: JSON.stringify(user),
      label: <UserOption {...user} />,
    };
  };

  const getUsers = useCallback(
    async ({ search, page, pageSize }: IInfiniteDebounecedAutocompleteSearchParams) => {
      const { data, success, errorMessage } = await searchUsers({
        search,
        page,
        per_page: pageSize,
      });

      if (success) {
        const users = data.data;
        const newOptions = users.map(user => generateOption(user));
        const isFirstPage = page === DefaultPagination.START_PAGE;

        setTotal(data.total);
        setOptions(prevOptions => (isFirstPage ? newOptions : [...prevOptions, ...newOptions]));
      } else {
        notificationApi.error({ title: t(errorMessage) });
      }
    },
    [notificationApi, t],
  );

  const handleSelect = async (value: string) => {
    const user = JSON.parse(value);

    const uid: string = user.uid || '';
    const username: string = user.username || '';

    const newValue = fullUser ? user : uid;

    onChange && onChange(newValue);
    onSelect && onSelect(newValue);
    setSearch(username);
  };

  const handleChange = async (value: string) => {
    setSearch(value);
  };

  const handleClear = async () => {
    if (value) {
      onChange && onChange(null);
      onSelect && onSelect(null);
      onClear && onClear();
    }
  };

  useEffect(() => {
    setSearch(username);
  }, [username]);

  return (
    <InfiniteDebouncedAutocomplete
      total={total}
      value={search}
      options={options}
      pageSize={PAGE_SIZE}
      placeholder={t(placeholder)}
      onSearch={getUsers}
      onSelect={handleSelect}
      onChange={handleChange}
      onClear={handleClear}
      {...props}
    />
  );
};

export default UsersAutocomplete;
